<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useDisplay } from 'vuetify'
import { useSessionStore } from './stores/session'

const session = useSessionStore()
const router = useRouter()
const route = useRoute()
const { mdAndUp } = useDisplay()
const drawer = ref(mdAndUp.value)
watch(mdAndUp, (desktop) => {
  drawer.value = desktop
})
const publicPage = computed(() => Boolean(route.meta.public))
const primaryLinks = [
  { title: 'Dashboard', icon: 'mdi-view-dashboard-outline', to: '/' },
  { title: 'Batch import', icon: 'mdi-playlist-plus', to: '/imports' },
  { title: 'Products', icon: 'mdi-tag-multiple-outline', to: '/products' },
  {
    title: 'Products without channels',
    icon: 'mdi-bell-off-outline',
    to: '/products?view=no_channels',
  },
]
const accountLinks = computed(() => [
  {
    title: 'Profile & notifications',
    subtitle: 'Email, Telegram & passkeys',
    icon: 'mdi-account-circle-outline',
    to: '/profile',
  },
  ...(session.isAdmin
    ? [
        {
          title: 'Administration',
          subtitle: 'Users, providers & health',
          icon: 'mdi-shield-account-outline',
          to: '/admin',
        },
      ]
    : []),
])
const pageTitle = computed(() => {
  if (route.path.startsWith('/admin')) return 'Administration'
  if (route.path.startsWith('/profile')) return 'Profile & notifications'
  if (route.path.startsWith('/products')) return 'Products'
  return 'PriceChime'
})
async function logout() {
  await session.logout()
  await router.push('/login')
}
</script>

<template>
  <v-app>
    <template v-if="!publicPage">
      <v-navigation-drawer
        v-model="drawer"
        :permanent="mdAndUp"
        color="#10231f"
        theme="dark"
        width="292"
      >
        <div class="pa-6">
          <div class="d-flex align-center ga-3">
            <v-avatar color="secondary" size="42"
              ><v-icon color="#10231f">mdi-bell-ring-outline</v-icon></v-avatar
            >
            <div>
              <div class="text-h6 font-weight-bold">PriceChime</div>
              <div class="text-caption text-medium-emphasis">Swiss price watch</div>
            </div>
          </div>
        </div>
        <v-list nav class="px-3">
          <v-list-item
            v-for="link in primaryLinks"
            :key="link.to"
            :to="link.to"
            :prepend-icon="link.icon"
            :title="link.title"
            rounded="lg"
          />
          <v-divider class="my-4" />
          <div class="text-overline text-medium-emphasis px-4 mb-1">Account & delivery</div>
          <v-list-item
            v-for="link in accountLinks"
            :key="link.to"
            :to="link.to"
            :prepend-icon="link.icon"
            :title="link.title"
            :subtitle="link.subtitle"
            color="secondary"
            rounded="lg"
            class="mb-1"
          />
        </v-list>
        <template #append>
          <div class="pa-4">
            <v-divider class="mb-4" />
            <v-btn to="/profile" block variant="text" class="justify-start mb-2 px-2" height="52">
              <v-avatar color="primary" size="34" class="mr-3">
                {{ session.user?.displayName?.[0] }}
              </v-avatar>
              <span class="text-left text-truncate">
                <span class="d-block text-body-2">{{ session.user?.displayName }}</span>
                <span class="d-block text-caption text-medium-emphasis"
                  >Profile & notifications</span
                >
              </span>
            </v-btn>
            <v-btn block variant="text" prepend-icon="mdi-logout" @click="logout">Sign out</v-btn>
          </div>
        </template>
      </v-navigation-drawer>
      <v-app-bar flat color="transparent" class="px-2 d-md-none">
        <v-app-bar-nav-icon aria-label="Open navigation" @click="drawer = !drawer" />
        <v-app-bar-title class="font-weight-bold">{{ pageTitle }}</v-app-bar-title>
        <v-btn
          v-if="session.isAdmin"
          to="/admin"
          icon="mdi-shield-account-outline"
          aria-label="Administration"
        />
        <v-btn
          to="/profile"
          icon="mdi-account-circle-outline"
          aria-label="Profile and notifications"
        />
      </v-app-bar>
    </template>
    <v-main><router-view /></v-main>
  </v-app>
</template>
